import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  DollarOutlined,
  FileAddOutlined,
  MoreOutlined,
  PercentageOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  feeApi,
  type CollectInput,
  type DiscountInput,
  type FeeFrequency,
  type Invoice,
  type PaymentMethod,
  type Receipt,
  type ReceiptLine,
  type StudentLedger,
  type WaiveInput,
} from '@/api/fee';
import { financeApi } from '@/api/finance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { downloadFile } from '@/shared/lib/download';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE_TRANSFER: 'Online transfer',
};

const FREQUENCY_LABEL: Record<FeeFrequency, string> = {
  MONTHLY: 'Monthly',
  ONE_TIME: 'One-time',
  PER_TERM: 'Per term',
};
const FREQUENCY_COLOR: Record<FeeFrequency, string> = {
  MONTHLY: 'processing',
  ONE_TIME: 'success',
  PER_TERM: 'warning',
};

/* ---------- Statement (invoices + receipts merged chronologically) ---------- */

interface StatementRow {
  key: string;
  id: number;
  kind: 'charge' | 'payment';
  sortDate: string;
  invoice?: Invoice;
  receipt?: Receipt;
  /** Signed effect on the outstanding balance; 0 for reversed rows. */
  effect: number;
  balance: number;
  reversed: boolean;
}

// One bank-statement-style list: charges (+) and payments (−) with a running
// outstanding balance. Advance kept on a receipt doesn't reduce outstanding,
// so it is excluded from the effect (shown as a sub-line instead). Reversed
// rows stay visible for the audit trail but don't move the balance.
function buildStatement(ledger: StudentLedger): StatementRow[] {
  const rows: StatementRow[] = [
    ...ledger.invoices.map((invoice) => ({
      key: `inv-${invoice.id}`,
      id: invoice.id,
      kind: 'charge' as const,
      sortDate: invoice.periodMonth ?? '0000-00-00',
      invoice,
      effect: invoice.reversed
        ? 0
        : Number(invoice.netAmount) - Number(invoice.waivedAmount),
      balance: 0,
      reversed: invoice.reversed,
    })),
    ...ledger.receipts.map((receipt) => ({
      key: `rcpt-${receipt.id}`,
      id: receipt.id,
      kind: 'payment' as const,
      sortDate: receipt.paymentDate,
      receipt,
      effect: receipt.reversed
        ? 0
        : -(Number(receipt.totalAmount) - Number(receipt.advanceAmount)),
      balance: 0,
      reversed: receipt.reversed,
    })),
  ].sort((a, b) => {
    const c = a.sortDate.localeCompare(b.sortDate);
    if (c !== 0) return c;
    if (a.kind !== b.kind) return a.kind === 'charge' ? -1 : 1;
    return a.id - b.id;
  });
  let balance = 0;
  for (const r of rows) {
    balance += r.effect;
    r.balance = balance;
  }
  return rows.reverse(); // newest first — top row's balance = current outstanding
}

export function FeeStudentPage() {
  const navigate = useNavigate();
  const params = useParams<{ enrollmentId: string }>();
  const enrollmentId = Number(params.enrollmentId);

  const ledgerQuery = useQuery({
    queryKey: ['fee', 'ledger', enrollmentId],
    queryFn: () => feeApi.ledger(enrollmentId),
    enabled: Number.isFinite(enrollmentId),
  });

  if (ledgerQuery.isLoading) return <Skeleton active paragraph={{ rows: 10 }} />;
  if (ledgerQuery.isError || !ledgerQuery.data) {
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load ledger"
        description={extractError(ledgerQuery.error)}
        action={<Button onClick={() => navigate('/fee/collect')}>Back</Button>}
      />
    );
  }

  return (
    <Ledger
      ledger={ledgerQuery.data}
      onRefetch={() => ledgerQuery.refetch()}
      updatedAt={ledgerQuery.dataUpdatedAt}
    />
  );
}

function Ledger({ ledger, onRefetch, updatedAt }: { ledger: StudentLedger; onRefetch: () => void; updatedAt: number }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FEE_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FEE_MGMT:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('FEE_MGMT:DELETE'));
  const canApprove = useAuthStore((s) => s.hasAuthority('FEE_MGMT:APPROVE'));

  const [search, setSearch] = useSearchParams();
  const [collectOpen, setCollectOpen] = useState(() => search.get('action') === 'collect');
  const [manualOpen, setManualOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [waiveTarget, setWaiveTarget] = useState<Invoice | null>(null);
  const [justCreated, setJustCreated] = useState<Receipt | null>(null);

  const closeCollect = (receipt?: Receipt) => {
    setCollectOpen(false);
    if (search.has('action')) {
      const next = new URLSearchParams(search);
      next.delete('action');
      setSearch(next, { replace: true });
    }
    if (receipt) setJustCreated(receipt);
  };
  const [reverseTarget, setReverseTarget] = useState<
    { type: 'invoice' | 'receipt'; id: number; label: string; lines?: ReceiptLine[]; advanceAmount?: string } | null
  >(null);

  const reverseInvoice = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => feeApi.reverseInvoice(id, reason),
    onSuccess: () => {
      message.success('Invoice reversed');
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      setReverseTarget(null);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const waive = useMutation({
    mutationFn: ({ id, body }: { id: number; body: WaiveInput }) => feeApi.waiveInvoice(id, body),
    onSuccess: () => {
      message.success('Waiver applied');
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      setWaiveTarget(null);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverseReceipt = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => feeApi.reverseReceipt(id, reason),
    onSuccess: () => {
      message.success('Receipt reversed');
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      setReverseTarget(null);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const removeDiscount = useMutation({
    mutationFn: (id: number) => feeApi.removeDiscount(id),
    onSuccess: () => {
      message.success('Discount removed');
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      qc.invalidateQueries({ queryKey: ['fee', 'structure', ledger.enrollmentId] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const structureQuery = useQuery({
    queryKey: ['fee', 'structure', ledger.enrollmentId],
    queryFn: () => feeApi.enrollmentStructure(ledger.enrollmentId),
  });
  const structureRows = structureQuery.data ?? [];

  const statementRows = buildStatement(ledger);

  const statementCols: TableProps<StatementRow>['columns'] = [
    {
      title: 'Date',
      width: 120,
      render: (_, row) =>
        row.kind === 'charge' ? (
          <Typography.Text type="secondary">
            {row.invoice!.periodMonth
              ? dayjs(row.invoice!.periodMonth).format('MMM YYYY')
              : 'One-time'}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            {dayjs(row.receipt!.paymentDate).format('D MMM YYYY')}
          </Typography.Text>
        ),
    },
    {
      title: 'Entry',
      render: (_, row) => {
        if (row.kind === 'charge') {
          const inv = row.invoice!;
          const paid = Number(inv.paidAmount);
          const due = Number(inv.dueAmount);
          return (
            <Space orientation="vertical" size={2}>
              <Space size={6} wrap>
                <Typography.Text strong delete={inv.reversed}>
                  {inv.feeTypeName ?? 'Fee'}
                </Typography.Text>
                {inv.manual && <Tag color="orange">Manual</Tag>}
                {!inv.reversed && due === 0 && <Tag color="success">paid</Tag>}
                {Number(inv.waivedAmount) > 0 && (
                  <Tooltip title={inv.waiverReason ?? ''}>
                    <Tag color="purple">Waived {PKR(inv.waivedAmount)}</Tag>
                  </Tooltip>
                )}
                {inv.reversed && (
                  <Tag color="default">
                    Reversed{inv.reversalReason ? ` · ${inv.reversalReason}` : ''}
                  </Tag>
                )}
              </Space>
              {Number(inv.discountAmount) > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Gross {PKR(inv.grossAmount)} − discount {PKR(inv.discountAmount)}
                </Typography.Text>
              )}
              {!inv.reversed && paid > 0 && due > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Paid {PKR(inv.paidAmount)} · due {PKR(inv.dueAmount)}
                </Typography.Text>
              )}
            </Space>
          );
        }
        const r = row.receipt!;
        return (
          <Space orientation="vertical" size={2}>
            <Space size={6} wrap>
              <Typography.Text strong delete={r.reversed}>
                Receipt #{r.receiptNumber}
              </Typography.Text>
              <Tag>{METHOD_LABEL[r.paymentMethod]}</Tag>
              {r.reversed && (
                <Tag color="default">
                  Reversed{r.reversalReason ? ` · ${r.reversalReason}` : ''}
                </Tag>
              )}
            </Space>
            {r.proofReference && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {r.proofReference}
              </Typography.Text>
            )}
            {r.lines.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {r.lines.map((l) => `${l.label}: ${PKR(l.amountPaid)}`).join(' · ')}
              </Typography.Text>
            )}
            {Number(r.advanceAmount) > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                PKR {PKR(r.advanceAmount)} kept as advance (not applied to invoices)
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Amount',
      width: 130,
      align: 'right',
      render: (_, row) =>
        row.kind === 'charge' ? (
          <Typography.Text strong delete={row.reversed}>
            + {PKR(row.reversed ? row.invoice!.netAmount : row.effect)}
          </Typography.Text>
        ) : (
          <Typography.Text strong delete={row.reversed} style={{ color: '#16a34a' }}>
            − {PKR(row.receipt!.totalAmount)}
          </Typography.Text>
        ),
    },
    {
      title: 'Balance',
      width: 130,
      align: 'right',
      render: (_, row) =>
        row.reversed ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          <Typography.Text type="secondary">{PKR(row.balance)}</Typography.Text>
        ),
    },
    {
      title: '',
      width: 56,
      align: 'right',
      render: (_, row) => {
        const items: { key: string; label: string; danger?: boolean; onClick: () => void }[] = [];
        if (row.kind === 'charge' && !row.reversed) {
          const inv = row.invoice!;
          if (canApprove && Number(inv.dueAmount) > 0) {
            items.push({ key: 'waive', label: 'Waive…', onClick: () => setWaiveTarget(inv) });
          }
          if (canEdit && Number(inv.paidAmount) === 0 && Number(inv.waivedAmount) === 0) {
            items.push({
              key: 'reverse',
              label: 'Reverse…',
              danger: true,
              onClick: () =>
                setReverseTarget({
                  type: 'invoice',
                  id: inv.id,
                  label: `${inv.feeTypeName ?? 'Invoice'}${inv.periodMonth ? ' · ' + dayjs(inv.periodMonth).format('MMM YYYY') : ''}`,
                }),
            });
          }
        }
        if (row.kind === 'payment') {
          const r = row.receipt!;
          items.push({
            key: 'print',
            label: 'Print receipt',
            onClick: () => window.open(`/fee/receipts/${r.id}/print`, '_blank', 'noopener'),
          });
          if (canEdit && !r.reversed) {
            items.push({
              key: 'reverse',
              label: 'Reverse…',
              danger: true,
              onClick: () =>
                setReverseTarget({
                  type: 'receipt',
                  id: r.id,
                  label: `Receipt #${r.receiptNumber}`,
                  lines: r.lines,
                  advanceAmount: r.advanceAmount,
                }),
            });
          }
        }
        if (items.length === 0) return null;
        return (
          <Dropdown
            menu={{ items: items.map(({ onClick, ...i }) => ({ ...i, onClick })) }}
            trigger={['click']}
          >
            <Button size="small" type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const openInvoices = ledger.invoices.filter((i) => !i.reversed && Number(i.dueAmount) > 0);
  const billed = Number(ledger.billed);
  const paid = Number(ledger.paid);
  const outstanding = Number(ledger.outstanding);
  const advanceBalance = Number(ledger.advanceBalance);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow={`Module / Fee · ${ledger.registrationNumber}`}
        title={`${ledger.firstName} ${ledger.lastName}`}
        description={
          <Space size={6} wrap>
            {ledger.className && (
              <Tag>{ledger.className} · {ledger.sectionName}</Tag>
            )}
            <Typography.Text type="secondary">Enrollment #{ledger.enrollmentId}</Typography.Text>
          </Space>
        }
        backTo="/fee/collect"
        onRefresh={onRefetch}
        refreshKey={['fee', 'ledger', ledger.enrollmentId]}
        updatedAt={updatedAt}
        actions={
          <Space>
            <VoucherDropdown
              enrollmentId={ledger.enrollmentId}
              onError={(msg) => message.error(msg)}
            />
            {canCreate && (
              <Button icon={<PercentageOutlined />} onClick={() => setDiscountOpen(true)}>
                Add discount
              </Button>
            )}
            {canCreate && (
              <Button icon={<FileAddOutlined />} onClick={() => setManualOpen(true)}>
                Add manual fee
              </Button>
            )}
            {canCreate && (
              <Button
                type="primary"
                icon={<DollarOutlined />}
                disabled={openInvoices.length === 0}
                onClick={() => setCollectOpen(true)}
              >
                Collect payment
              </Button>
            )}
          </Space>
        }
      />

      {justCreated && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setJustCreated(null)}
          title={`Receipt #${justCreated.receiptNumber} created · PKR ${PKR(justCreated.totalAmount)} · ${METHOD_LABEL[justCreated.paymentMethod]}`}
          description="Hand the printable copy to the parent."
          action={
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={() => window.open(`/fee/receipts/${justCreated.id}/print`, '_blank', 'noopener')}
            >
              Print receipt
            </Button>
          }
        />
      )}

      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 32 }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Outstanding
            </Typography.Text>
            <Typography.Title
              level={2}
              style={{ margin: 0, color: outstanding > 0 ? '#dc2626' : '#16a34a' }}
            >
              PKR {PKR(outstanding)}
            </Typography.Title>
          </div>
          <div style={{ flex: 1, minWidth: 220, maxWidth: 420 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Paid PKR {PKR(paid)} of PKR {PKR(billed)} billed
            </Typography.Text>
            <Progress
              percent={billed > 0 ? Math.round((paid / billed) * 100) : 0}
              showInfo={false}
              strokeColor="#16a34a"
            />
          </div>
          {advanceBalance > 0 && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Advance in hand
              </Typography.Text>
              <Typography.Title level={4} style={{ margin: 0 }}>
                PKR {PKR(advanceBalance)}
              </Typography.Title>
            </div>
          )}
        </div>
      </Card>

      <Card title="Fee plan">
        {structureQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : structureRows.length === 0 ? (
          <Empty description="No fee structure assigned to this student's class — set it up under Fee → Fee structures" />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {structureRows.map((row) => {
              const rowDiscounts = ledger.discounts.filter((d) => d.feeTypeId === row.feeTypeId);
              return (
                <div
                  key={row.feeTypeId}
                  style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
                >
                  <Typography.Text strong>{row.feeTypeName ?? '—'}</Typography.Text>
                  {row.frequency && (
                    <Tag color={FREQUENCY_COLOR[row.frequency]}>{FREQUENCY_LABEL[row.frequency]}</Tag>
                  )}
                  {Number(row.discountAmount) > 0 ? (
                    <Typography.Text type="secondary">
                      {PKR(row.amount)} − {PKR(row.discountAmount)} discount =
                    </Typography.Text>
                  ) : null}
                  <Typography.Text strong>
                    PKR {PKR(row.netAmount)}
                    {row.frequency === 'MONTHLY' ? ' / month' : ''}
                  </Typography.Text>
                  {rowDiscounts.map((d) => (
                    <Tooltip key={d.id} title={d.remarks ?? undefined}>
                      <Tag color="green" style={{ marginInlineEnd: 0 }}>
                        {d.discountTypeName ?? 'Discount'} · −
                        {d.percentage ? `${Number(d.value)}%` : PKR(d.value)}
                        {canDelete && (
                          <Popconfirm
                            title="Remove discount?"
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => removeDiscount.mutate(d.id)}
                          >
                            <DeleteOutlined style={{ marginLeft: 6 }} />
                          </Popconfirm>
                        )}
                      </Tag>
                    </Tooltip>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Statement" styles={{ body: { padding: 0 } }}>
        {statementRows.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty description="No activity yet — generate monthly invoices or add a manual fee" />
          </div>
        ) : (
          <Table
            rowKey="key"
            size="small"
            columns={statementCols}
            dataSource={statementRows}
            // Each row carries its precomputed running balance, so paging
            // doesn't break the balance column.
            pagination={{ pageSize: 30, hideOnSinglePage: true, showSizeChanger: false }}
          />
        )}
      </Card>

      {/* Modals */}
      {collectOpen && (
        <CollectModal
          ledger={ledger}
          onClose={closeCollect}
        />
      )}
      {manualOpen && (
        <ManualInvoiceModal
          enrollmentId={ledger.enrollmentId}
          existingInvoices={ledger.invoices}
          onClose={() => setManualOpen(false)}
        />
      )}
      {discountOpen && (
        <DiscountModal
          enrollmentId={ledger.enrollmentId}
          invoices={ledger.invoices}
          onClose={() => setDiscountOpen(false)}
        />
      )}
      {reverseTarget && (
        <ReverseModal
          target={reverseTarget}
          onClose={() => setReverseTarget(null)}
          submit={(reason) => {
            if (reverseTarget.type === 'invoice') {
              reverseInvoice.mutate({ id: reverseTarget.id, reason });
            } else {
              reverseReceipt.mutate({ id: reverseTarget.id, reason });
            }
          }}
          submitting={reverseInvoice.isPending || reverseReceipt.isPending}
        />
      )}
      {waiveTarget && (
        <WaiveModal
          invoice={waiveTarget}
          onClose={() => setWaiveTarget(null)}
          submit={(body) => waive.mutate({ id: waiveTarget.id, body })}
          submitting={waive.isPending}
        />
      )}
    </div>
  );
}

interface WaiveFormShape {
  amount: number;
  reason: string;
}

function WaiveModal({
  invoice,
  onClose,
  submit,
  submitting,
}: {
  invoice: Invoice;
  onClose: () => void;
  submit: (body: WaiveInput) => void;
  submitting: boolean;
}) {
  const [form] = Form.useForm<WaiveFormShape>();
  const due = Number(invoice.dueAmount);
  const label = `${invoice.feeTypeName ?? 'Invoice'}${invoice.periodMonth ? ' · ' + dayjs(invoice.periodMonth).format('MMM YYYY') : ''}`;

  return (
    <Modal
      open
      title={`Waive · ${label}`}
      okText="Apply waiver"
      okButtonProps={{ danger: true, loading: submitting }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Forgives an unpaid portion of this invoice. Outstanding now: <b>Rs {PKR(due)}</b>. The original
        gross/discount stay intact; the waiver is recorded with your name &amp; reason for audit.
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ amount: due }}
        onFinish={(values) =>
          submit({ amount: String(values.amount), reason: values.reason.trim() })
        }
      >
        <Form.Item
          label="Amount to waive (Rs)"
          name="amount"
          rules={[
            { required: true, message: 'Required' },
            { type: 'number', min: 0.01, max: due, message: `Must be between 0.01 and ${due}` },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0.01} max={due} step={1} />
        </Form.Item>
        <Form.Item
          label="Reason"
          name="reason"
          rules={[{ required: true, max: 1000 }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Why is this being waived?" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ---------- Collect modal ---------- */

interface CollectFormShape {
  paymentDate: Dayjs;
  paymentMethod: PaymentMethod;
  bankAccountId?: number;
  proofReference?: string;
  amountReceived: number;
  paying: Record<number, number>;
}

// Oldest-first: ONE_TIME (no periodMonth) first, then by periodMonth ascending,
// then by invoice id as a stable tiebreaker.
function sortOldestFirst(invs: Invoice[]): Invoice[] {
  return [...invs].sort((a, b) => {
    if (!a.periodMonth && b.periodMonth) return -1;
    if (a.periodMonth && !b.periodMonth) return 1;
    if (a.periodMonth && b.periodMonth) {
      const c = a.periodMonth.localeCompare(b.periodMonth);
      if (c !== 0) return c;
    }
    return a.id - b.id;
  });
}

function fifoAllocate(amount: number, invs: Invoice[]): Record<number, number> {
  let remaining = Math.max(0, amount);
  const out: Record<number, number> = {};
  for (const i of sortOldestFirst(invs)) {
    const due = Number(i.dueAmount);
    if (remaining <= 0) { out[i.id] = 0; continue; }
    const take = Math.min(due, remaining);
    out[i.id] = Math.round(take * 100) / 100;
    remaining -= take;
  }
  return out;
}

function CollectModal({ ledger, onClose }: { ledger: StudentLedger; onClose: (receipt?: Receipt) => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<CollectFormShape>();

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts', 'active'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);

  const openInvoices = sortOldestFirst(
    ledger.invoices.filter((i) => !i.reversed && Number(i.dueAmount) > 0),
  );
  const totalOutstanding = openInvoices.reduce((s, i) => s + Number(i.dueAmount), 0);
  const initialPaying: Record<number, number> = Object.fromEntries(openInvoices.map((i) => [i.id, 0]));

  const collect = useMutation({
    mutationFn: (body: CollectInput) => feeApi.collect(body),
    onSuccess: (receipt) => {
      message.success(`Receipt #${receipt.receiptNumber} created`);
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      onClose(receipt);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedMethod = Form.useWatch('paymentMethod', form);
  const watchedReceived = Form.useWatch('amountReceived', form) ?? 0;
  const watchedPaying = Form.useWatch('paying', form) ?? {};

  const allocated = Object.values(watchedPaying).reduce(
    (s, v) => s + (Number(v) || 0),
    0,
  );
  const payingCount = Object.values(watchedPaying).filter((v) => Number(v) > 0).length;
  const advance = Math.max(0, Number(watchedReceived) - allocated);
  const overAllocated = allocated > Number(watchedReceived) + 0.001;

  // Once a line is hand-edited, stop overwriting it when the amount changes.
  const [manualEdited, setManualEdited] = useState(false);

  // Pre-select the first cash drawer — cash is the quick path. When the bank
  // list is already cached the value goes in via initialValues (the effect
  // fires before the Modal's form is connected, so setFieldValue would be
  // dropped); the effect covers the first-ever open where banks load late.
  const defaultCashId = banks.find((b) => b.cash)?.id;
  useEffect(() => {
    if (
      defaultCashId &&
      form.getFieldValue('paymentMethod') === 'CASH' &&
      !form.getFieldValue('bankAccountId')
    ) {
      form.setFieldValue('bankAccountId', defaultCashId);
    }
  }, [defaultCashId, form]);

  const runAllocate = (amount: number) => {
    form.setFieldValue('paying', fifoAllocate(amount, openInvoices));
  };

  const payEverything = () => {
    form.setFieldValue('amountReceived', Math.round(totalOutstanding * 100) / 100);
    form.setFieldValue(
      'paying',
      Object.fromEntries(openInvoices.map((i) => [i.id, Number(i.dueAmount)])),
    );
    setManualEdited(false);
  };

  const onFinish = (values: CollectFormShape) => {
    const lines = Object.entries(values.paying ?? {})
      .filter(([, v]) => Number(v) > 0)
      .map(([id, v]) => ({ invoiceId: Number(id), amountPaid: Number(v).toFixed(2) }));
    const sumLines = lines.reduce((acc, l) => acc + Number(l.amountPaid), 0);
    const received = Number(values.amountReceived) || 0;
    if (received <= 0) {
      message.error('Enter the amount received from the parent.');
      return;
    }
    if (sumLines > received + 0.001) {
      message.error('Allocated amount cannot exceed amount received.');
      return;
    }
    if (!values.bankAccountId) {
      message.error('Pick the cash drawer or bank account that received the payment.');
      return;
    }
    const adv = Math.max(0, received - sumLines);

    collect.mutate({
      enrollmentId: ledger.enrollmentId,
      paymentDate: values.paymentDate.format('YYYY-MM-DD'),
      paymentMethod: values.paymentMethod,
      bankAccountId: values.bankAccountId,
      proofReference: values.proofReference?.trim() || undefined,
      totalAmount: received.toFixed(2),
      advanceAmount: adv.toFixed(2),
      lines,
    });
  };

  const noBanks = !banksQuery.isLoading && banks.length === 0;

  return (
    <Modal
      title={`Collect payment · ${ledger.firstName} ${ledger.lastName}`}
      open
      onCancel={() => onClose()}
      onOk={() => form.submit()}
      confirmLoading={collect.isPending}
      okText="Record receipt"
      okButtonProps={{ disabled: noBanks || overAllocated }}
      width={760}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          paymentDate: dayjs(),
          paymentMethod: 'CASH' as PaymentMethod,
          bankAccountId: defaultCashId,
          paying: initialPaying,
        }}
        onFinish={onFinish}
      >
        {noBanks && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title="No cash drawers or bank accounts configured"
            description="Add one under Finance → Bank Accounts before recording a receipt — every collection needs a posting account."
          />
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Total outstanding · {openInvoices.length} invoice(s)
            </Typography.Text>
            <Typography.Title level={3} type="danger" style={{ margin: 0 }}>
              PKR {PKR(totalOutstanding)}
            </Typography.Title>
          </div>
          <Button onClick={payEverything}>Pay everything</Button>
        </div>

        <Form.Item
          label="Amount received from parent"
          name="amountReceived"
          rules={[{ required: true, type: 'number', min: 0.01, message: 'Required' }]}
          tooltip="The actual cash / transfer amount. It is paid into the oldest invoices first; any excess is kept as advance."
        >
          <InputNumber
            size="large"
            autoFocus
            min={0}
            step={500}
            style={{ width: '100%' }}
            prefix="Rs"
            placeholder="0"
            onChange={(v) => {
              if (!manualEdited) runAllocate(Number(v) || 0);
            }}
          />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Payment date" name="paymentDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Method" name="paymentMethod" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => ({
                  value: m,
                  label: METHOD_LABEL[m],
                }))}
                onChange={(m: PaymentMethod) =>
                  form.setFieldValue(
                    'bankAccountId',
                    banks.find((b) => (m === 'CASH' ? b.cash : !b.cash))?.id,
                  )
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label={watchedMethod === 'CASH' ? 'Cash drawer' : 'Bank account'}
              name="bankAccountId"
              rules={[{ required: true, message: 'Required — drives the GL posting' }]}
              tooltip="Posts a debit to this account so the receipt shows on trial balance / cash & bank books."
            >
              <Select
                loading={banksQuery.isLoading}
                placeholder={banks.length === 0 ? 'No accounts configured' : '—'}
                options={banks
                  .filter((b) => (watchedMethod === 'CASH' ? b.cash : !b.cash))
                  .map((b) => ({
                    value: b.id,
                    label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}`,
                  }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label={watchedMethod === 'CASH' ? 'Reference (optional)' : 'Proof reference'}
              name="proofReference"
              rules={[{ required: watchedMethod !== 'CASH', message: 'Required for bank/online transfers', max: 200 }]}
              tooltip={
                watchedMethod === 'CASH'
                  ? 'Optional note or slip number for this cash payment.'
                  : 'Deposit slip number or bank/online transaction ID.'
              }
            >
              <Input
                placeholder={
                  watchedMethod === 'CASH' ? 'Slip / note (optional)' : 'TX#1234567 or deposit slip number'
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          size="small"
          items={[
            {
              key: 'allocation',
              // Keep the per-invoice fields mounted while collapsed — otherwise
              // they never register with the form and onFinish gets no `paying`
              // values, sending the whole amount to advance.
              forceRender: true,
              label: (
                <Typography.Text>
                  Allocation — paying {payingCount} of {openInvoices.length} invoice(s)
                  <Typography.Text type="secondary"> · adjust manually</Typography.Text>
                </Typography.Text>
              ),
              children: (
                <>
                  <Space wrap style={{ marginBottom: 8 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        runAllocate(Number(watchedReceived) || 0);
                        setManualEdited(false);
                      }}
                    >
                      Auto-allocate (oldest first)
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => {
                        form.setFieldValue('paying', initialPaying);
                        setManualEdited(true);
                      }}
                    >
                      Clear allocation
                    </Button>
                  </Space>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={openInvoices}
                    columns={[
                      {
                        title: 'Fee',
                        render: (_, row) => (
                          <Space orientation="vertical" size={0}>
                            <span>{row.feeTypeName}</span>
                            {row.periodMonth && (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(row.periodMonth).format('MMM YYYY')}
                              </Typography.Text>
                            )}
                          </Space>
                        ),
                      },
                      {
                        title: 'Due',
                        width: 120,
                        align: 'right',
                        render: (_, row) => (
                          <Typography.Text strong>PKR {PKR(row.dueAmount)}</Typography.Text>
                        ),
                      },
                      {
                        title: 'Paying',
                        width: 180,
                        render: (_, row) => (
                          <Form.Item name={['paying', row.id]} noStyle>
                            <InputNumber
                              min={0}
                              max={Number(row.dueAmount)}
                              step={100}
                              style={{ width: '100%' }}
                              placeholder="0"
                              onChange={() => setManualEdited(true)}
                            />
                          </Form.Item>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />

        <Alert
          type={overAllocated ? 'error' : 'info'}
          showIcon
          style={{ marginTop: 8 }}
          title={
            overAllocated
              ? `Allocated PKR ${PKR(allocated)} is more than received PKR ${PKR(Number(watchedReceived))}. Reduce a line.`
              : `Received PKR ${PKR(Number(watchedReceived))} · Allocated PKR ${PKR(allocated)} across ${Object.values(watchedPaying).filter((v) => Number(v) > 0).length} invoice(s) · Advance kept: PKR ${PKR(advance)}`
          }
        />
      </Form>
    </Modal>
  );
}

/* ---------- Manual invoice modal ---------- */

interface ManualFormShape {
  feeTypeId: number;
  periodMonth?: Dayjs;
  amount: number;
  reason: string;
  allowDuplicate: boolean;
}

function ManualInvoiceModal({
  enrollmentId,
  existingInvoices,
  onClose,
}: {
  enrollmentId: number;
  existingInvoices: Invoice[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<ManualFormShape>();

  const { data: feeTypes } = useQuery({
    queryKey: ['fee', 'types', 'active'],
    queryFn: () => feeApi.listTypes(true),
    staleTime: 5 * 60 * 1000,
  });

  const submit = useMutation({
    mutationFn: (values: ManualFormShape) =>
      feeApi.addManualInvoice({
        enrollmentId,
        feeTypeId: values.feeTypeId,
        periodMonth: values.periodMonth ? values.periodMonth.startOf('month').format('YYYY-MM-DD') : undefined,
        amount: values.amount.toFixed(2),
        reason: values.reason.trim(),
        allowDuplicate: values.allowDuplicate,
      }),
    onSuccess: () => {
      message.success('Manual fee added');
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', enrollmentId] });
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedFeeTypeId = Form.useWatch('feeTypeId', form);
  const watchedPeriod = Form.useWatch('periodMonth', form);
  const watchedAllowDup = Form.useWatch('allowDuplicate', form);

  const existingMatch = watchedFeeTypeId
    ? existingInvoices.find((i) => {
        if (i.reversed) return false;
        if (i.feeTypeId !== watchedFeeTypeId) return false;
        const targetPeriod = watchedPeriod ? watchedPeriod.startOf('month').format('YYYY-MM-DD') : null;
        return (i.periodMonth ?? null) === targetPeriod;
      })
    : null;

  return (
    <Modal
      title="Add manual fee"
      open
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={submit.isPending}
      okText={existingMatch && !watchedAllowDup ? 'Cannot add (duplicate)' : 'Add'}
      okButtonProps={{ disabled: !!existingMatch && !watchedAllowDup }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ allowDuplicate: false } as Partial<ManualFormShape>}
        onFinish={(values) => submit.mutate(values)}
      >
        <Form.Item label="Fee type" name="feeTypeId" rules={[{ required: true }]}>
          <Select
            options={(feeTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          label="Period (optional)"
          name="periodMonth"
          help="Leave blank for a one-time charge (damages, late fee). Set a month for fees tied to a specific period (e.g. Library makeup for May)."
        >
          <DatePicker.MonthPicker style={{ width: '100%' }} format="MMM YYYY" />
        </Form.Item>
        {existingMatch && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title="An invoice for this fee type and period already exists"
            description={
              <Space orientation="vertical" size={6}>
                <span>
                  {existingMatch.feeTypeName}
                  {existingMatch.periodMonth ? ` · ${dayjs(existingMatch.periodMonth).format('MMM YYYY')}` : ' · One-time'}
                  : net PKR {PKR(existingMatch.netAmount)} (paid PKR {PKR(existingMatch.paidAmount)}, due PKR {PKR(existingMatch.dueAmount)})
                </span>
                <Form.Item name="allowDuplicate" valuePropName="checked" noStyle>
                  <Switch
                    checkedChildren="Yes, add anyway"
                    unCheckedChildren="Block duplicate"
                  />
                </Form.Item>
              </Space>
            }
          />
        )}
        <Form.Item
          label="Amount (PKR)"
          name="amount"
          rules={[{ required: true, type: 'number', min: 0 }]}
        >
          <InputNumber style={{ width: '100%' }} step={100} min={0} />
        </Form.Item>
        <Form.Item
          label="Reason"
          name="reason"
          rules={[{ required: true, min: 1, max: 1000 }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="e.g. Lab equipment damage replacement" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ---------- Discount modal ---------- */

interface DiscountFormShape {
  feeTypeId: number;
  discountTypeId: number;
  percentage: boolean;
  value: number;
  remarks?: string;
}

function DiscountModal({
  enrollmentId,
  invoices,
  onClose,
}: {
  enrollmentId: number;
  invoices: Invoice[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<DiscountFormShape>();

  // Discountable fee types: those assigned via the class·section structure, plus types
  // already billed on this enrollment (manual fees negotiated after the fact). Anything
  // else would never bill, and the backend rejects it anyway.
  const { data: structureRows } = useQuery({
    queryKey: ['fee', 'structure', enrollmentId],
    queryFn: () => feeApi.enrollmentStructure(enrollmentId),
  });
  const { data: discountTypes } = useQuery({
    queryKey: ['setup', 'lookups', 'discount-types', 'active'],
    queryFn: () => setupApi.listLookup('discount-types', true),
    staleTime: 5 * 60 * 1000,
  });

  const submit = useMutation({
    mutationFn: (values: DiscountFormShape) => {
      const body: DiscountInput = {
        feeTypeId: values.feeTypeId,
        discountTypeId: values.discountTypeId,
        percentage: values.percentage,
        value: values.value.toFixed(2),
        remarks: values.remarks?.trim() || undefined,
      };
      return feeApi.addDiscount(enrollmentId, body);
    },
    onSuccess: (res) => {
      if (res.invoicesUpdated > 0) {
        message.success(`Discount added — ${res.invoicesUpdated} invoice${res.invoicesUpdated === 1 ? '' : 's'} updated`);
      } else {
        message.warning(
          'Discount saved, but no open invoice was updated. Generate an invoice for this fee type, or reverse a paid/waived one first.',
          6,
        );
      }
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', enrollmentId] });
      qc.invalidateQueries({ queryKey: ['fee', 'structure', enrollmentId] });
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedPercentage = Form.useWatch('percentage', form);

  const assignedFeeTypes = structureRows ?? [];
  const assignedIds = new Set(assignedFeeTypes.map((t) => t.feeTypeId));
  const billedOnly = new Map<number, string>();
  for (const inv of invoices) {
    if (!inv.reversed && !assignedIds.has(inv.feeTypeId)) {
      billedOnly.set(inv.feeTypeId, inv.feeTypeName ?? `#${inv.feeTypeId}`);
    }
  }
  const feeTypeOptions = [
    ...assignedFeeTypes.map((t) => ({
      value: t.feeTypeId,
      label: `${t.feeTypeName ?? t.feeTypeId} · PKR ${PKR(t.amount)}`,
    })),
    ...[...billedOnly.entries()].map(([id, name]) => ({
      value: id,
      label: `${name} · billed`,
    })),
  ];

  return (
    <Modal
      title="Add discount"
      open
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={submit.isPending}
      okText="Add"
      okButtonProps={{ disabled: feeTypeOptions.length === 0 }}
      destroyOnHidden
    >
      {structureRows && feeTypeOptions.length === 0 && (
        <Alert
          type="warning"
          showIcon
          message="Nothing to discount yet"
          description="Discounts apply to fees assigned to the class or already billed. Set up the class fee structure under Fee → Fee structures, or add a manual fee first."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ percentage: true, value: 10 } as Partial<DiscountFormShape>}
        onFinish={(values) => submit.mutate(values)}
      >
        <Form.Item label="On fee type" name="feeTypeId" rules={[{ required: true }]}>
          <Select
            options={feeTypeOptions}
            showSearch
            optionFilterProp="label"
            placeholder={feeTypeOptions.length === 0 ? 'No discountable fees' : '—'}
          />
        </Form.Item>
        <Form.Item label="Discount type" name="discountTypeId" rules={[{ required: true }]}>
          <Select
            options={(discountTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item label="As percentage" name="percentage" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          label={watchedPercentage ? 'Percentage (0–100)' : 'Fixed amount (PKR)'}
          name="value"
          rules={[
            { required: true, type: 'number', min: 0.01 },
            { type: 'number', max: watchedPercentage ? 100 : 1_000_000 },
          ]}
        >
          <InputNumber style={{ width: '100%' }} step={watchedPercentage ? 1 : 100} />
        </Form.Item>
        <Form.Item label="Remarks" name="remarks" rules={[{ max: 1000 }]}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ---------- Voucher dropdown ---------- */

function VoucherDropdown({
  enrollmentId,
  onError,
}: {
  enrollmentId: number;
  onError: (msg: string) => void;
}) {
  const now = dayjs();
  // Offer current month plus the 3 prior months and the next one — most cashier prints
  // are for "this month" with the occasional reprint of last month.
  const offsets = [1, 0, -1, -2, -3];
  const months = offsets.map((delta) => now.add(delta, 'month'));

  const downloadVoucher = async (month: dayjs.Dayjs) => {
    const m = month.format('YYYY-MM');
    try {
      await downloadFile(
        `/reports/exports/fee-voucher/${enrollmentId}.pdf`,
        { month: m },
        `fee-voucher-e${enrollmentId}-${m}.pdf`,
      );
    } catch (e) {
      onError(extractError(e, 'Voucher download failed'));
    }
  };

  return (
    <Dropdown.Button
      icon={<PrinterOutlined />}
      onClick={() => downloadVoucher(now)}
      menu={{
        items: months.map((m) => ({
          key: m.format('YYYY-MM'),
          label: `${m.format('MMM YYYY')}${m.isSame(now, 'month') ? ' · this month' : ''}`,
        })),
        onClick: ({ key }) => downloadVoucher(dayjs(key + '-01')),
      }}
    >
      Print voucher
    </Dropdown.Button>
  );
}

/* ---------- Reverse modal ---------- */

function ReverseModal({
  target,
  onClose,
  submit,
  submitting,
}: {
  target: { type: 'invoice' | 'receipt'; id: number; label: string; lines?: ReceiptLine[]; advanceAmount?: string };
  onClose: () => void;
  submit: (reason: string) => void;
  submitting: boolean;
}) {
  const [reason, setReason] = useState('');
  const lines = target.lines ?? [];
  const totalPaid = lines.reduce((s, l) => s + Number(l.amountPaid), 0);
  const advance = Number(target.advanceAmount ?? 0);
  const isAdvanceOnly = target.type === 'receipt' && lines.length === 0 && advance > 0;

  let warningDescription: string;
  if (target.type === 'invoice') {
    warningDescription = 'The original invoice stays for audit; subsequent computations exclude it.';
  } else if (isAdvanceOnly) {
    warningDescription = `The original receipt stays for audit. The Rs ${PKR(advance)} advance kept on account will be removed and the ledger postings flipped.`;
  } else {
    warningDescription = 'The original receipt stays for audit. Each invoice paid by this receipt will become outstanding again.';
  }

  return (
    <Modal
      title={`Reverse ${target.type}: ${target.label}`}
      open
      onCancel={onClose}
      okText="Reverse"
      okButtonProps={{ danger: true, disabled: !reason.trim() || submitting }}
      confirmLoading={submitting}
      onOk={() => submit(reason.trim())}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        title="Reversal is permanent"
        description={warningDescription}
        style={{ marginBottom: 16 }}
      />
      {target.type === 'receipt' && lines.length > 0 && (
        <Card
          size="small"
          title={`These invoices will become outstanding again · PKR ${PKR(totalPaid)}`}
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: 0 } }}
        >
          <Table
            rowKey="invoiceId"
            size="small"
            pagination={false}
            dataSource={lines}
            columns={[
              { title: 'Invoice', dataIndex: 'label' },
              {
                title: 'Reopens',
                dataIndex: 'amountPaid',
                width: 140,
                align: 'right',
                render: (a: string) => `PKR ${PKR(a)}`,
              },
            ]}
          />
        </Card>
      )}
      <label style={{ display: 'block', fontWeight: 500, marginBottom: 6 }}>Reason (required)</label>
      <Input.TextArea
        autoFocus
        autoSize={{ minRows: 2, maxRows: 4 }}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}
